import React, { useEffect, useRef, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";
import PricingCard from "./PricingCard";
import { HotelType } from "../../../../backend/src/shared/types";
import { useAppContext } from "../../contexts/AppContext";
import { orderBookingForm } from "../../api-client";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { useHotelContext } from "../../contexts/HotelContext";

interface PricingProps {
  formData: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  date: string;
  totalCost: number;
  adultCount: number;
  childCount: number;
  hotel: HotelType;
  hold: string;
}

const Pricing: React.FC<PricingProps> = ({ formData, onUpdate, onNext, totalCost, hotel, hold, date }) => {
  //const totalCount = adultCount + childCount || 1; 
  const depositPrice = ((totalCost * 0.2)).toFixed(2);
  const fullPayment = (totalCost); 
  const { isLoggedIn } = useAppContext();
  const partner_order_id = uuidv4();
  const { book_hash } = useParams<{ book_hash: string }>();

  const [selectedPlan, setSelectedPlan] = useState<string>(formData?.selectedPlan || ""); // Persist selected plan
  const hasCalled = useRef(false); 
  const { saveBookingFormResponse, cancellationPenalties } = useHotelContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sendOrderBookingForm = async (params: { 
    partner_order_id: string;
    book_hash: string;
    language: string;
    user_ip: string;
  }) => {
    try{
      const bookingForm = await orderBookingForm(params);
      console.log(bookingForm);

      saveBookingFormResponse(bookingForm);

    } catch (error) {
      console.error("Failed to send Booking Form", error);
    }
  };

  useEffect(() => {
    if (!hasCalled.current) {
      sendOrderBookingForm({
        partner_order_id: partner_order_id,
        book_hash: book_hash || "",
        language: "en",
        user_ip: "3.75.158.163"
      });
      hasCalled.current = true;
    }
  }, []);

  useEffect(() => {
    const handleSignInSuccess = (event: MessageEvent) => {
      if (event.data.type === "SIGN_IN_SUCCESS") {
        const { state } = event.data;
        onUpdate(state);
        setSelectedPlan(state.selectedPlan);
        window.location.reload(); // Refresh the page to update the state
      }
    };

    window.addEventListener("message", handleSignInSuccess);
    return () => {
      window.removeEventListener("message", handleSignInSuccess);
    };
  }, [onUpdate]);

  const handleSelectPlan = (plan: string, selectedPrice: number | string, hotel: HotelType) => {
    setSelectedPlan(plan); // Update local state
    onUpdate({
      selectedPlan: plan,
      totalCost,
      depositPrice,
      fullPayment,
      selectedPrice,
      hotel: hotel,
    });
    onNext();
  };

  const parseDate = (dateStr: string) => {
    // Remove ordinal suffix (st, nd, rd, th)
    const cleanedDateStr = dateStr.replace(/\b(\d{1,2})(st|nd|rd|th)\b/, "$1");
    return new Date(cleanedDateStr);
  };

  const datePass = Boolean(date && (parseDate(date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) < 39);

  const handleFreeFareHoldClick = () => {
    if (!isLoggedIn) {
      localStorage.setItem("bookingState", JSON.stringify({
        from: window.location.pathname, // Current path
        formData, // Preserve current form data
        totalCost,
        hotel,
      }));
      window.open("/sign-in-form", "_blank");
      return;
    }
    handleSelectPlan("Free Fare Hold", totalCost, hotel);
  };

  const pricingPlans = [

    {
      title: "BIG Savings",
      features: [
        { text: "£250 additional discount", icon: "/featureIcons/point-tick.svg" },
        { text: "£125 voucher for your next trip", icon: "/featureIcons/point-tick.svg" },
        { text: "Flexible payment options", icon: "/featureIcons/point-x.svg" },
      ],
      priceInfo: `£${fullPayment}`,
      subtitle: "Pay in full only",
      buttonText: "Select",
      isHighlighted: selectedPlan === "BIG Savings",
      isBestValue: false,
      isSelected: selectedPlan === "BIG Savings",
      datePass: false,
      onClick: () => handleSelectPlan("BIG Savings", fullPayment, hotel),
    },
    {
      title: "Easy Payment Plan",
      features: [
        { text: "£60 Voucher for your next trip", icon: "/featureIcons/point-tick.svg" },
        { text: "Flexible payment options", icon: "/featureIcons/point-tick.svg" },
        { text: "No discount", icon: "/featureIcons/point-x.svg" },
      ],
      priceInfo: `£${depositPrice}`,
      subtitle: "Begin with a deposit — and pay monthly",
      buttonText: "Select",
      isHighlighted: selectedPlan === "Easy Payment Plan",
      isBestValue: true,
      isSelected: selectedPlan === "Easy Payment Plan",
      datePass: false,
      onClick: () => handleSelectPlan("Easy Payment Plan", depositPrice, hotel),
    },
    ...(hold !== "hold"
      ? [
        {
          title: "Free Fare Hold",
          features: [
            { text: "96 Hour Free Fare Hold", icon: "/featureIcons/point-tick.svg" },
            { text: "No discount", icon: "/featureIcons/point-x.svg" },
            { text: "No voucher", icon: "/featureIcons/point-x.svg" },
          ],
          priceInfo: "Reserve now, pay later",
          subtitle: "No payment, no rush, no stress",
          buttonText: "Select",
          isHighlighted: selectedPlan === "Free Fare Hold",
          isBestValue: false,
          isSelected: selectedPlan === "Free Fare Hold",
          datePass: datePass,
          cancellationPenalties: cancellationPenalties.free_cancellation_before,
          onClick: () => handleFreeFareHoldClick(),
        },
      ]
      : []),

  ];

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Pricing | Halo Holidays</title>
          <meta
            name="description"
            content="Already a member? Sign in to your Halo Holidays account to manage your bookings, view your travel history, and access exclusive deals."
          />
        </Helmet>
      </HelmetProvider>

      <section className="w-full my-4 mt-16 px-2 md:px-0">
        <div className="flex justify-center bg-white">
          <div className="flex flex-col md:flex-row space-x-0 md:space-x-4 w-full">
            {pricingPlans.map((plan, index) => (
              <PricingCard key={index} {...plan} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
